import { useRef, useMemo } from 'react';

const DefaultGetId = o => o.id;

function useComputedProperties(objects, props, computedProperties, getId = DefaultGetId) {
  //
  // Compute dependent properties used by computed properties
  //
  // Get the names of dependent properties from provided computedProperties array
  const depPropNames = useRef(computedProperties.reduce((all, cp) => {
    return all.concat(cp.dependentPropertyNames
      ? cp.dependentPropertyNames.filter(p => (all.indexOf(p) < 0)) : []);
  }, []));
  // Calc the dependent properties object and check it against the last computed one
  // so that we can memoize next the computation of the computed properties
  const depPropsRef = useRef();
  let depProps = depPropsRef.current;
  let diff = false;
  const newProps = {};
  depPropNames.current.forEach((n) => {
    newProps[n] = props[n];
    if (!depProps || (newProps[n] !== depProps[n])) {
      diff = true;
    }
  });
  // depProps contains the properties computed properties depend on
  // and is a new object only if at least one the props has changed.
  depProps = diff ? newProps : depProps;

  //
  // Compute computed properties
  //
  const objsByIds = useRef({});
  const cpObjsByIds = useRef({});
  const objectsCache = useRef();
  const computedObjects = useMemo(() => {
    const oldObjByIds = objsByIds.current;
    const oldCPObjByIds = cpObjsByIds.current;
    const newObjByIds = {};
    const newCPObjByIds = {};
    let changed = false;
    const cpObjects = objects.map((o) => {
      const id = getId(o);
      const oldObject = oldObjByIds[id];
      let objectChanged = !oldObject || (oldObject !== o);
      const oldCPObject = !objectChanged && oldCPObjByIds[id];
      const cpProps = {};
      computedProperties.forEach((cp) => {
        const v = cp.compute(o, depProps);
        cpProps[cp.name] = v;
        if (!objectChanged && (oldCPObject[cp.name] !== v)) {
          objectChanged = true;
        }
      });
      if (objectChanged) {
        const cpO = Object.assign(cpProps, o);
        changed = true;
        newObjByIds[id] = o;
        newCPObjByIds[id] = cpO;
        return cpO;
      }
      newObjByIds[id] = oldObject;
      newCPObjByIds[id] = oldCPObject;
      return oldCPObject;
    });
    objsByIds.current = newObjByIds;
    cpObjsByIds.current = newCPObjByIds;
    if (changed || !objectsCache.current || (objectsCache.current.length !== objects.length)) {
      objectsCache.current = cpObjects;
      return cpObjects;
    }
    return objectsCache.current;
  }, [objects, depProps, getId]);

  return computedObjects;
}

export default useComputedProperties;
